import React, { useState, useEffect } from 'react';
import { Card, Tabs, Typography, List, Tag, Button, Modal, Form, Input, InputNumber, Select, message } from 'antd';
import { HeartOutlined, ClockCircleOutlined, StarOutlined, PlusOutlined, EditOutlined } from '@ant-design/icons';
import { apiMethods } from '../services/api';
import { Zikr as ZikrType } from '../types/api';
import ScheduleGrid from './ScheduleGrid';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

export const Zikr: React.FC = () => {
  const [selectedZikr, setSelectedZikr] = useState<ZikrType | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [zikrList, setZikrList] = useState<{
    morning_evening: ZikrType[];
    after_prayer: ZikrType[];
    anytime: ZikrType[];
  }>({
    morning_evening: [],
    after_prayer: [],
    anytime: []
  });

  const fetchZikr = async () => {
    try {
      setLoading(true);
      const [morningEvening, afterPrayer, anytime] = await Promise.all([
        apiMethods.getZikr('morning_evening'),
        apiMethods.getZikr('after_prayer'),
        apiMethods.getZikr('anytime')
      ]);
      setZikrList({
        morning_evening: morningEvening,
        after_prayer: afterPrayer,
        anytime: anytime
      });
    } catch (error) {
      message.error('Failed to load zikr records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZikr();
  }, []);

  // Reset create form when modal opens
  useEffect(() => {
    if (isCreateModalVisible) {
      form.resetFields();
      // Set default values but don't select category by default
      form.setFieldsValue({
        category: undefined,
        arabic: '',
        transliteration: '',
        translation: '',
        count: undefined,
        virtue: '',
        visibilitySchedule: null,
      });
    }
  }, [isCreateModalVisible, form]);

  type ZikrFormType = Omit<ZikrType, 'id'> & { visibilitySchedule?: { [day: string]: number[] } | null };
  const handleCreate = async (values: ZikrFormType) => {
    try {
      // Ensure visibility_schedule is included
      const payload = { ...values, visibility_schedule: values.visibilitySchedule ?? null };
      delete payload.visibilitySchedule;
      await apiMethods.createZikr(payload);
      message.success('Zikr created successfully');
      setIsCreateModalVisible(false);
      form.resetFields();
      fetchZikr();
    } catch (error) {
      message.error('Failed to create zikr');
    }
  };

  const handleEdit = async (values: ZikrFormType) => {
    try {
      if (!selectedZikr?.id) {
        throw new Error('No Zikr selected for editing');
      }
      const payload = { ...values, visibility_schedule: values.visibilitySchedule ?? null };
      delete payload.visibilitySchedule;
      await apiMethods.updateZikr(selectedZikr.id, payload);
      message.success('Zikr updated successfully');
      setIsModalVisible(false);
      setIsEditing(false);
      editForm.resetFields();
      fetchZikr();
    } catch (error) {
      message.error('Failed to update zikr');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await apiMethods.deleteZikr(id);
      message.success('Zikr deleted successfully');
      setIsModalVisible(false);
      fetchZikr();
    } catch (error) {
      message.error('Failed to delete zikr');
    }
  };

  const startEditing = () => {
    if (selectedZikr) {
      editForm.setFieldsValue({
        category: selectedZikr.category,
        arabic: selectedZikr.arabic,
        transliteration: selectedZikr.transliteration || '',
        translation: selectedZikr.translation,
        count: selectedZikr.count,
        virtue: selectedZikr.virtue || '',
        display_duration: selectedZikr.display_duration || 20,
        visibilitySchedule: (selectedZikr as any).visibility_schedule || null,
      });
      setIsEditing(true);
    }
  };

  const renderZikrList = (category: keyof typeof zikrList) => (
    <List
      itemLayout="vertical"
      dataSource={zikrList[category]}
      loading={loading}
      style={{ 
        height: '100%',
        overflow: 'auto'
      }}
      renderItem={zikr => (
        <List.Item
          style={{
            padding: '16px',
            marginBottom: '8px'
          }}
          actions={[
            <Button 
              type="link" 
              onClick={() => {
                setSelectedZikr(zikr);
                setIsModalVisible(true);
              }}
              icon={<StarOutlined />}
            >
              View Details
            </Button>
          ]}
        >
          <List.Item.Meta
            title={
              <div>
                {zikr.arabic && (
                  <div style={{ 
                    fontSize: '1.5em', 
                    marginBottom: 8
                  }}>
                    {zikr.arabic}
                  </div>
                )}
                <div>{zikr.transliteration}</div>
              </div>
            }
            description={zikr.translation}
          />
          <div style={{ marginTop: '8px' }}>
            {zikr.count && zikr.count > 0 && <Tag color="blue">{zikr.count}x</Tag>}
            <Tag color="orange">{zikr.display_duration || 20}s</Tag>
          </div>
        </List.Item>
      )}
    />
  );

  return (
    <div style={{ 
      maxWidth: 1200, 
      margin: '0 auto',
      height: '100vh',  // Use viewport height instead of minHeight
      padding: '24px'
    }}>
      <Card style={{ 
        height: '100%'  // Fill container height
      }}>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column',
          height: '100%'  // Fill card height
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
            <div>
              <Title level={3}>Daily Zikr</Title>
              <Text>Collection of daily remembrance and supplications</Text>
            </div>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setIsCreateModalVisible(true)}
            >
              Add New Zikr
            </Button>
          </div>

          <Tabs 
            defaultActiveKey="1" 
            style={{ 
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              height: 0  // This is crucial - allows flex child to scroll
            }}
          >
            <TabPane
              tab={<span><ClockCircleOutlined /> Morning & Evening</span>}
              key="1"
              style={{ height: '100%', overflow: 'auto' }}
            >
              {renderZikrList('morning_evening')}
            </TabPane>
            <TabPane
              tab={<span><HeartOutlined /> After Prayer</span>}
              key="2"
              style={{ height: '100%', overflow: 'auto' }}
            >
              {renderZikrList('after_prayer')}
            </TabPane>
            <TabPane
              tab={<span><StarOutlined /> Anytime</span>}
              key="3"
              style={{ height: '100%', overflow: 'auto' }}
            >
              {renderZikrList('anytime')}
            </TabPane>
          </Tabs>
        </div>
      </Card>

      <Modal
        title="Zikr Details"
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setIsEditing(false);
          editForm.resetFields();
        }}
        footer={
          isEditing ? null : [
            <Button key="edit" type="primary" icon={<EditOutlined />} onClick={startEditing}>
              Edit
            </Button>,
            <Button key="delete" danger onClick={() => selectedZikr?.id && handleDelete(selectedZikr.id)}>
              Delete
            </Button>,
            <Button key="close" onClick={() => setIsModalVisible(false)}>
              Close
            </Button>
          ]
        }
      >
        {selectedZikr && !isEditing ? (
          <>
            {selectedZikr.arabic && (
              <Paragraph style={{ fontSize: '1.5em', textAlign: 'center' }}>
                {selectedZikr.arabic}
              </Paragraph>
            )}
            {selectedZikr.transliteration && (
              <Paragraph>
                <strong>Transliteration:</strong><br />
                {selectedZikr.transliteration}
              </Paragraph>
            )}
            <Paragraph>
              <strong>Translation:</strong><br />
              {selectedZikr.translation}
            </Paragraph>
            {selectedZikr.count && selectedZikr.count > 0 && (
              <Paragraph>
                <strong>Count:</strong><br />
                {selectedZikr.count}x
              </Paragraph>
            )}
            {selectedZikr.virtue && (
              <Paragraph>
                <strong>Virtue:</strong><br />
                {selectedZikr.virtue}
              </Paragraph>
            )}
            <Paragraph>
              <strong>Display Duration:</strong><br />
              {selectedZikr.display_duration || 20} seconds
            </Paragraph>
          </>
        ) : (
          <Form
            form={editForm}
            layout="vertical"
            onFinish={handleEdit}
          >
            <Form.Item
              name="category"
              label="Category"
              rules={[{ required: true, message: 'Please select a category' }]}
            >
              <Select placeholder="Select a category">
                <Option value="morning_evening">Morning & Evening</Option>
                <Option value="after_prayer">After Prayer</Option>
                <Option value="anytime">Anytime</Option>
              </Select>
            </Form.Item>

            <Form.Item
              name="arabic"
              label="Arabic Text"
              rules={[{ required: true, message: 'Please enter Arabic text' }]}
            >
              <Input.TextArea rows={2} placeholder="Enter Arabic text" />
            </Form.Item>

            <Form.Item
              name="transliteration"
              label="Transliteration"
            >
              <Input placeholder="Enter transliteration (optional)" />
            </Form.Item>

            <Form.Item
              name="translation"
              label="Translation"
            >
              <Input.TextArea rows={2} placeholder="Enter translation (optional)" />
            </Form.Item>

            <Form.Item
              name="count"
              label="Count"
            >
              <InputNumber placeholder="Enter count (optional)" />
            </Form.Item>

            <Form.Item
              name="virtue"
              label="Virtue"
            >
              <Input.TextArea rows={2} placeholder="Enter virtue (optional)" />
            </Form.Item>

            <Form.Item
              name="display_duration"
              label="Display Duration (seconds)"
              tooltip="How long this zikr should be displayed in the carousel"
            >
              <InputNumber 
                min={1} 
                max={60} 
                placeholder="Duration in seconds (default: 20)" 
                style={{ width: '100%' }}
              />
            </Form.Item>

            <Form.Item
              name="visibilitySchedule"
              label="Visibility Schedule"
            >
              <ScheduleGrid />
            </Form.Item>

            <Form.Item>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setIsEditing(false);
                  editForm.resetFields();
                }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit">
                  Save Changes
                </Button>
              </div>
            </Form.Item>
          </Form>
        )}
      </Modal>

      <Modal
        title="Add New Zikr"
        open={isCreateModalVisible}
        onCancel={() => {
          setIsCreateModalVisible(false);
          form.resetFields();
        }}
        footer={null}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreate}
        >
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: 'Please select a category' }]}
          >
            <Select placeholder="Select a category">
              <Option value="morning_evening">Morning & Evening</Option>
              <Option value="after_prayer">After Prayer</Option>
              <Option value="anytime">Anytime</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="arabic"
            label="Arabic Text"
            rules={[{ required: true, message: 'Please enter Arabic text' }]}
          >
            <Input.TextArea rows={2} placeholder="Enter Arabic text" />
          </Form.Item>

          <Form.Item
            name="transliteration"
            label="Transliteration"
          >
            <Input placeholder="Enter transliteration (optional)" />
          </Form.Item>

          <Form.Item
            name="translation"
            label="Translation"
          >
            <Input.TextArea rows={2} placeholder="Enter translation (optional)" />
          </Form.Item>

          <Form.Item
            name="count"
            label="Count"
          >
            <InputNumber placeholder="Enter count (optional)" />
          </Form.Item>

          <Form.Item
            name="virtue"
            label="Virtue"
          >
            <Input.TextArea rows={2} placeholder="Enter virtue (optional)" />
          </Form.Item>

          <Form.Item
            name="display_duration"
            label="Display Duration (seconds)"
            tooltip="How long this zikr should be displayed in the carousel"
          >
            <InputNumber 
              min={1} 
              max={60} 
              placeholder="Duration in seconds (default: 20)" 
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="visibilitySchedule"
            label="Visibility Schedule"
          >
            <ScheduleGrid />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block>
              Create Zikr
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}; 